import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Calendar, Coins, Receipt, Settings, LogOut, BadgeCheck, Mail, Smartphone, LifeBuoy } from "lucide-react";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";

const TABS = [
  { to: "/profile/bookings", label: "My bookings", icon: <Calendar size={14} />, testid: "profile-tab-bookings" },
  { to: "/profile/esim", label: "My eSIMs", icon: <Smartphone size={14} />, testid: "profile-tab-esim" },
  ...(FRACTIONAL_ENABLED ? [{ to: "/profile/invested", label: "Investments", icon: <Coins size={14} />, testid: "profile-tab-invested" }] : []),
  { to: "/profile/messages", label: "Email & SMS", icon: <Mail size={14} />, testid: "profile-tab-messages" },
  { to: "/profile/support", label: "Support cases", icon: <LifeBuoy size={14} />, testid: "profile-tab-support" },
  { to: "/profile/transactions", label: "Transaction history", icon: <Receipt size={14} />, testid: "profile-tab-transactions" },
  { to: "/profile/settings", label: "Profile settings", icon: <Settings size={14} />, testid: "profile-tab-settings" },
];

const navLinkClass = ({ isActive }) =>
  "flex items-center gap-2.5 px-3 h-10 rounded-lg text-sm transition " +
  (isActive
    ? "bg-[var(--zc-accent-soft)] text-[var(--zc-accent)] font-semibold"
    : "text-[var(--zc-text-2)] hover:bg-[var(--zc-bg-3)] hover:text-[var(--zc-text)]");

const mobileTabClass = ({ isActive }) =>
  "flex items-center gap-2 px-3 h-11 rounded-lg text-xs font-semibold border transition-colors min-w-0 " +
  (isActive
    ? "bg-[var(--zc-accent-soft)] border-[var(--zc-accent)] text-[var(--zc-accent)]"
    : "bg-white border-[var(--zc-border)] text-[var(--zc-text-2)] hover:bg-[var(--zc-bg-3)]");

export default function ProfileLayout({ children, title, eyebrow }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 min-w-0" data-testid="profile-layout">
      <div className="lg:hidden space-y-4 mb-6">
        <div className="zc-card p-3 flex items-center gap-3 min-w-0">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 grid place-items-center rounded-full bg-[var(--zc-accent-soft)] text-[var(--zc-accent)] font-bold text-sm shrink-0">
              {user?.name?.[0] || "Z"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user?.display_name || user?.name}</div>
            <div className="text-[10px] text-[var(--zc-text-3)] truncate flex items-center gap-1">
              <BadgeCheck size={10} style={{ color: user?.kyc_status === "approved" ? "var(--zc-success)" : "var(--zc-text-3)" }} />
              {user?.kyc_status === "approved" ? "Verified" : "Unverified"} · {user?.role}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-lg text-[var(--zc-text-3)] hover:bg-[var(--zc-bg-3)] hover:text-[var(--zc-text)]"
              aria-label="Account dashboard"
              data-testid="profile-back-dash-mobile"
            >
              <Settings size={16} />
            </button>
            <button
              type="button"
              onClick={logout}
              className="p-2 rounded-lg text-[var(--zc-text-3)] hover:bg-[var(--zc-bg-3)] hover:text-[var(--zc-text)]"
              aria-label="Sign out"
              data-testid="profile-logout-mobile"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        <nav className="grid grid-cols-2 gap-2" data-testid="profile-nav-mobile">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} className={mobileTabClass} data-testid={t.testid}>
              <span className="shrink-0">{t.icon}</span>
              <span className="truncate min-w-0 flex-1">{t.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="grid lg:grid-cols-[280px,1fr] gap-8">
        <aside className="hidden lg:block lg:sticky lg:top-24 h-fit space-y-4">
          <div className="zc-card p-5">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 grid place-items-center rounded-full bg-[var(--zc-accent-soft)] text-[var(--zc-accent)] font-bold text-lg">
                  {user?.name?.[0] || "Z"}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{user?.display_name || user?.name}</div>
                <div className="text-xs text-[var(--zc-text-3)] truncate">{user?.email}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-caption" style={{ color: user?.kyc_status === "approved" ? "var(--zc-success)" : "var(--zc-text-3)" }}>
              <BadgeCheck size={11} />
              {user?.kyc_status === "approved" ? "Verified" : "Unverified"} · {user?.role}
            </div>
          </div>

          <nav className="zc-card p-3" data-testid="profile-nav">
            {TABS.map((t) => (
              <NavLink key={t.to} to={t.to} className={navLinkClass} data-testid={t.testid}>
                {t.icon}
                {t.label}
              </NavLink>
            ))}
          </nav>

          <div className="zc-card p-3">
            <button onClick={() => navigate("/dashboard")} className="w-full flex items-center gap-2.5 px-3 h-10 rounded-lg text-sm text-[var(--zc-text-2)] hover:bg-[var(--zc-bg-3)]" data-testid="profile-back-dash">
              <Settings size={14} /> Account dashboard
            </button>
            <button onClick={logout} className="w-full flex items-center gap-2.5 px-3 h-10 rounded-lg text-sm text-[var(--zc-text-2)] hover:bg-[var(--zc-bg-3)]" data-testid="profile-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        <main className="min-w-0">
          {(eyebrow || title) && (
            <div className="mb-8">
              {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
              {title && <h1 className="zc-headline text-4xl md:text-5xl">{title}</h1>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
